import { AuthProvider, type TAuthConfig } from '@sangoma/react-oauth2-code-pkce';
import { QueryClientProvider } from '@tanstack/react-query';
import React, { type ReactNode, useEffect, useState } from 'react';
import ReactDom from 'react-dom/client';
import { createBrowserRouter, createMemoryRouter, RouterProvider } from 'react-router-dom';
import { appRoutes } from './App';
import { GlobalErrorBoundary } from './components/GlobalErrorBoundary';
import { LoadingPage } from './components/LoadingPage/LoadingPage';
import { MeetingSwitchPrompt } from './components/MeetingSwitchDialog/MeetingSwitchPrompt';
import { OverlayProvider } from './components/overlays';
import { getAuthConfig } from './config/authConfig';
import { queryClient } from './config/react-query';
import { GuestSessionProvider } from './contexts/GuestSessionContext';
import { PopupBlockedProvider } from './contexts/PopupBlockedContext';
import { ScheduledMeetingsProvider } from './contexts/ScheduledMeetingsContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { UserProvider } from './contexts/UserContext';
import { UserPermissionsProvider } from './contexts/UserPermissionsContext';
import { hostPathToInternalEntry } from './mf/hostPath';
import { authStorageService } from './services/authStorageService';
import { setFederatedMode } from './services/hostAuthService';
import { meetingSessionService } from './services/meetingSessionService';
import { isMeetingPopup, latchMeetingPopupIdentity } from './services/meetingWindow/popupMode';
import { initClientMetadata } from './telemetry/clientMetadata';
import { initTelemetry } from './telemetry/otelClient';
import { retrieveJitsiMeetId } from './utils/jitsiAPIUtils';

// `__webpack_public_path__` is the rspack runtime global. In MF mode the
// rspack config sets `output.publicPath: 'auto'`, which resolves at runtime
// to the origin the bundle was served from (e.g. `http://localhost:8081/`).
declare const __webpack_public_path__: string;

let root: ReactDom.Root | null = null;

/**
 * The data-router instance type. `react-router-dom` v7 does not export this
 * shape under a stable public name, so we derive it from the factory's return
 * type — `createBrowserRouter` and `createMemoryRouter` both produce it.
 */
type DataRouter = ReturnType<typeof createBrowserRouter>;

/**
 * Options the host passes when it mounts NG Meet as a Module Federation
 * remote. Mirrors the host-driven contract used by `teamhub-meet` /
 * `teamhub-chat`.
 */
export type MountOptions = {
  /**
   * A pre-created data router supplied by the host. When omitted (the usual
   * hosted case) we build a memory router seeded from `initialPath` so the
   * host keeps ownership of the browser URL bar.
   */
  defaultRouter?: DataRouter;
  /** Invoked on every NG Meet navigation so the host can sync its URL bar. */
  onNavigate?: (state: { location: { pathname: string } }) => void;
  /** The host's current full path at mount time (e.g. `/meet/settings`). */
  initialPath?: string;
  /** The host sub-path NG Meet is mounted under (e.g. `/meet`). */
  containerAppSuffix?: string;
  /** Host header height in px, if the host wants us to offset our layout. */
  headerHeight?: number;
};

/** Handles the host uses to drive navigation into, and tear down, the remote. */
export type MountResult = {
  /** Called by the host when ITS location changes, to push NG Meet's router. */
  onParentNavigate: (location?: { pathname: string }) => void;
  /** Unmounts NG Meet and releases the router subscription. */
  unmount: () => void;
};

/**
 * The post-auth provider stack — same for standalone and hosted modes. The
 * only thing that differs between modes is what supplies tokens to
 * `<UserProvider>` (see `src/contexts/UserContext.tsx`).
 */
const renderAuthedTree = (children: ReactNode): ReactNode => (
  <UserProvider>
    <GuestSessionProvider>
      <QueryClientProvider client={queryClient}>
        <UserPermissionsProvider>
          <SettingsProvider>
            <ScheduledMeetingsProvider>
              <OverlayProvider>
                <PopupBlockedProvider>
                  {children}
                  {/* Inert outside a meeting popup; see MeetingSwitchPrompt. */}
                  <MeetingSwitchPrompt />
                </PopupBlockedProvider>
              </OverlayProvider>
            </ScheduledMeetingsProvider>
          </SettingsProvider>
        </UserPermissionsProvider>
      </QueryClientProvider>
    </GuestSessionProvider>
  </UserProvider>
);

// The standalone PKCE config is resolved asynchronously (it derives a device
// id). Resolve it once on mount and show the loading page until it's ready.
// Hosted mode never renders `<AuthConfig>`, so the work never runs there.
const AuthConfig: React.FC<{ children: (authConfig: TAuthConfig) => ReactNode }> = ({
  children,
}) => {
  const [authConfig, setAuthConfig] = useState<TAuthConfig | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getAuthConfig().then((config) => {
      if (!cancelled) {
        setAuthConfig(config);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!authConfig) {
    return <LoadingPage message={'Loading Sangoma Meet..'} />;
  }

  return <>{children(authConfig)}</>;
};

/**
 * The router + render-error boundary. The `<RouterProvider>` replaces the old
 * `<BrowserRouter><Routes>` tree; `GlobalErrorBoundary` used to live inside
 * `<App>` and still catches route render errors from here.
 */
const routerContent = (router: DataRouter): ReactNode => (
  <GlobalErrorBoundary
    onError={(error, errorInfo) => {
      console.error('Global error caught:', error, errorInfo);
    }}
  >
    <RouterProvider router={router} />
  </GlobalErrorBoundary>
);

/**
 * Render `tree` into `rootElement`. `.ng-meet-app` is the scoping root that
 * the SCSS pipeline wraps every component selector under (see
 * `static/styles/main.scss`).
 */
const renderTree = (rootElement: HTMLElement, tree: ReactNode, hosted = false): void => {
  root = ReactDom.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <div className={hosted ? 'ng-meet-app ng-meet-app--hosted' : 'ng-meet-app'}>{tree}</div>
    </React.StrictMode>,
  );
};

// MF mode only: the host loads our `./app` chunk via its manifest but does not
// load our `index.html`, so the `<link>` auto-generated by HtmlRspackPlugin
// never reaches the host's DOM. Inject it at runtime, resolved against the
// bundle's origin so it works regardless of which TeamHub instance loaded us.
const ensureHostedStylesheet = (): void => {
  if (process.env.RUN_MODE !== 'remote' || typeof document === 'undefined') {
    return;
  }
  if (document.querySelector('link[data-ngmeet-css="true"]')) {
    return;
  }
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `${__webpack_public_path__}main.css`;
  link.setAttribute('data-ngmeet-css', 'true');
  document.head.appendChild(link);
};

// Suppress harmless "ResizeObserver loop" errors (a known browser quirk) so
// they don't trip the dev error overlay. Scoped to the standalone entry — we
// must not patch the host page's `console.error` / `window` in hosted mode.
let resizeObserverSuppressionInstalled = false;
const installResizeObserverErrorSuppression = (): void => {
  if (resizeObserverSuppressionInstalled || typeof window === 'undefined') {
    return;
  }
  resizeObserverSuppressionInstalled = true;

  const originalError = console.error;
  console.error = (...args: unknown[]) => {
    if (typeof args[0] === 'string' && args[0].includes('ResizeObserver loop')) {
      return;
    }
    originalError.apply(console, args);
  };

  window.addEventListener('error', (event) => {
    if (event.message?.includes('ResizeObserver loop')) {
      event.stopImmediatePropagation();
      event.preventDefault();
      return false;
    }
  });
};

/**
 * Module Federation entry. TeamHub imports this module as `NgMeet/app` and
 * calls `mount(hostElement, options)`. NG Meet runs on a memory router (unless
 * the host supplies its own), reports navigations through `onNavigate`, and
 * returns handles so the host can drive navigation and unmount us.
 *
 * Hosted mode does NOT wrap the tree in `<AuthProvider>` — `UserProvider`'s
 * hosted branch reads tokens from the host bridge (see `useHostAuth`), which
 * also performs its own readiness wait.
 */
export const mount = (
  rootElement: HTMLElement,
  { defaultRouter, onNavigate, initialPath, containerAppSuffix }: MountOptions = {},
): MountResult => {
  setFederatedMode(true);

  // Telemetry is scoped to Meet-specific domains and page-wide side effects
  // (RUM/session replay, console forwarding) are skipped so we don't capture
  // the host's activity (SM-1107).
  initTelemetry(true);
  void initClientMetadata(retrieveJitsiMeetId());

  ensureHostedStylesheet();

  // Map the host's path to NG Meet's internal route by stripping the mount
  // sub-path (e.g. host `/meet/settings` -> NG Meet `/settings`). The SAME
  // mapping is reused by `onParentNavigate` below so parent-driven navigation
  // stays in sync with the initial entry.
  const initialEntry = hostPathToInternalEntry(initialPath, containerAppSuffix);

  const router = defaultRouter ?? createMemoryRouter(appRoutes, { initialEntries: [initialEntry] });

  const unsubscribe = router.subscribe((state) => {
    onNavigate?.(state);
  });

  try {
    renderTree(rootElement, renderAuthedTree(routerContent(router)), true);
  } catch (error) {
    console.error('Render failed', error);
    throw error;
  }

  return {
    onParentNavigate: (location) => {
      if (location) {
        void router.navigate(hostPathToInternalEntry(location.pathname, containerAppSuffix));
      }
    },
    unmount: () => {
      unsubscribe();
      root?.unmount();
      root = null;
    },
  };
};

// Public standalone pages that must NOT be wrapped by AuthProvider. The OAuth
// library runs token-refresh / redirect logic on mount, which would redirect
// unauthenticated visitors away from these pages.
const PUBLIC_STANDALONE_PATHS = ['/dids'];

/**
 * Standalone entry. Used when the NG Meet bundle is loaded directly (the
 * shipping meetings.sangoma.com app, or the MF bundle served at :8081 for
 * debugging). Keeps NG Meet's own PKCE OAuth flow and a browser router.
 */
export const mountStandalone = (rootElement: HTMLElement): void => {
  setFederatedMode(false);

  // Must happen before React mounts: `useMeetingLink` strips the popup query
  // params in an effect, and the popup flag and opener origin are only readable
  // while they are still on the URL.
  latchMeetingPopupIdentity();

  initTelemetry(false);
  void initClientMetadata(retrieveJitsiMeetId());

  installResizeObserverErrorSuppression();

  const isPublicStandalonePage = PUBLIC_STANDALONE_PATHS.includes(window.location.pathname);
  // Meeting popup launched by a hosted NG Meet instance: identity is handed in
  // from the opener over postMessage (see UserContext's PopupHandoffUserProvider),
  // so PKCE must NOT run here — a second OAuth redirect would conflict.
  const isPopupMeetingWindow = isMeetingPopup();

  // PKCE-specific bookkeeping; skipped for public pages and the meeting popup
  // because AuthProvider never mounts in those cases.
  if (!(isPublicStandalonePage || isPopupMeetingWindow)) {
    // Must run before AuthProvider mounts to prevent "Bad authorization state" error.
    authStorageService.clearStaleLoginState();
    meetingSessionService.observeCrossTabAuthChange();
  }

  const router = createBrowserRouter(appRoutes);

  try {
    if (isPublicStandalonePage) {
      // Public standalone page: bypass auth providers entirely.
      renderTree(
        rootElement,
        <QueryClientProvider client={queryClient}>{routerContent(router)}</QueryClientProvider>,
      );
      return;
    }

    if (isPopupMeetingWindow) {
      // No PKCE wrapper: UserProvider's popup branch supplies the identity.
      renderTree(rootElement, renderAuthedTree(routerContent(router)));
      return;
    }

    // Standalone authed: resolve the PKCE config asynchronously, then wrap the
    // tree in PKCE's `<AuthProvider>`.
    renderTree(
      rootElement,
      <AuthConfig>
        {(authConfig) => (
          <AuthProvider authConfig={authConfig}>
            {renderAuthedTree(routerContent(router))}
          </AuthProvider>
        )}
      </AuthConfig>,
    );
  } catch (error) {
    console.error('Render failed', error);
    throw error;
  }
};
